// Need to use the React-specific entry point to import createApi
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { RootState } from "redux-rtk/rootReducer";
import { toast } from "react-toastify";
import queryString from 'query-string';
import { flatten } from 'q-flat'

import type { BaseQueryFn } from '@reduxjs/toolkit/query'
import axios from 'axios'
import type { AxiosRequestConfig, AxiosError } from 'axios'

enum ActionTypes {
  REHYDRATE = "REHYDRATE",
}
interface Headers {
  [key: string]: string;
}
interface IAxiosBaseQuery {
  baseUrl?: string;
  headers?: Headers;
}

const axiosBaseQuery =
  (
    { baseUrl, headers }: IAxiosBaseQuery
  ): BaseQueryFn<
    {
      url: string
      method: AxiosRequestConfig['method']
      data?: AxiosRequestConfig['data']
      params?: AxiosRequestConfig['params']
    },
    unknown,
    unknown
  > =>
    async ({ url, method, data, params }, { signal, getState }) => {
      try {
        let uState = (getState() as RootState)?.Auth.user;
        let mainHeaders = {
          'Accept' : 'application/json',
          'Authorization': `Bearer ${uState.token}`
        };

        const result = await axios({ url: baseUrl + url, method, data, params, 
          headers: {
            ...headers, ...mainHeaders
          },
          transformResponse: [function (data) {
            let res = JSON.parse(data);
            if(res.error){
              toast.error(res.error, { autoClose: 10000 });
            }
            if(res.errors){
              toast.error(res.message, { autoClose: 10000 });
            }
            // if(res.message){
            //   toast.error(res.message, { autoClose: 10000 });
            // }
            return res;
          }],
        })
        return { data: result.data }
      } catch (axiosError) {
        if (typeof axiosError === 'string') {
          // toast.error(axiosError, { autoClose: 10000 });
          switch (axiosError) {
            case 'Invalid credentials':
              window.location.href = "/auth/login";
              break;
          
            default:
              break;
          }
        }
        let err = axiosError as AxiosError
        return {
          error: {
            status: err?.response?.status,
            data: err?.response?.data || err?.message,
          },
        }
      }
    }

// Define a service using a base URL and expected endpoints
export const api = createApi({
  reducerPath: "api",
  tagTypes: [
    "Auth",
    "NKBMenu",
    "NKBDashboard",
    "Order",
    "Expenses",
    "MDataBrand",
    "MDataClient",
    "MDataProduct",
    "MDataStaff",
    "MDataPartner",
    "MDataVendor",
    "MDataTypeOfExpenses",
    "AccessControlRole",
    "AccessControlPermission",
    "AccessControlUser",
    "Notification",
    "Deposit",
    "Balance",
    "BCA",
    "FileDownload",

    "NKBCustomer",
    "NKBWishlist"
  ],
  // keepUnusedDataFor: 1,
  // baseQuery: fetchBaseQuery({
  //   baseUrl: process.env.REACT_APP_API_URL,
  //   timeout: 2000,
  //   prepareHeaders: (headers, { getState }) => {
  //     let uState = (getState() as RootState)?.Auth.user;
  //     if (typeof uState === "object" && uState !== null) {
  //       if (uState.token) {
  //         headers.set("authorization", `Bearer ${uState.token}`);
  //       }
  //     }

  //     headers.set("Accept", "application/json");

  //     return headers;
  //   },
  // }),
  baseQuery: axiosBaseQuery({
    baseUrl: process.env.REACT_APP_API_URL || '',
  }),
  extractRehydrationInfo(action, { reducerPath }) {
    if (action.type === ActionTypes.REHYDRATE) {
      return action.payload[reducerPath];
    }
  },
  endpoints: (builder) => ({
    // Auth
    // loginUser: builder.mutation({
    //   query: (payload) => ({
    //     url: "/auth/login",
    //     method: "POST",
    //     data: payload,
    //   }),
    //   invalidatesTags: ["Auth"],
    // }),

    getMenu: builder.query({
      query: (params) => ({
        url: "/menus",
        params: params,
        method: "GET",
      }),
      providesTags: ["NKBMenu"],
      // keepUnusedDataFor: 1,
    }),

    getDashboard: builder.query({
      query: (params) => ({
        url: "/dashboards",
        params: params,
        method: "GET",
      }),
      providesTags: ["NKBDashboard"],
      // keepUnusedDataFor: 1,
    }),

    // Order
    getOrder: builder.query({
      query: (params) => ({
        url: "/orders" + (params ? "?"+new URLSearchParams(params).toString() : ""),
        method: "GET",
      }),
      providesTags: ["Order"],
      // keepUnusedDataFor: 1,
    }),
    addOrder: builder.mutation({
      query: (payload) => ({
        url: "/orders/create",
        method: "POST",
        data: payload,
        headers: { "Content-Type": "multipart/form-data" },
      }),
      invalidatesTags: ["Order"],
    }),
    updateOrder: builder.mutation({
      query: (payload) => ({
        url: "/orders/update",
        method: "POST",
        data: payload,
        headers: { "Content-Type": "multipart/form-data" },
      }),
      invalidatesTags: ["Order"],
    }),
    deleteOrder: builder.mutation({
      query: (payload) => ({
        url: "/orders/delete",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["Order"],
    }),

    // Order
    getExpenses: builder.query({
      query: (params) => ({
        // url: "/expenses" + (params ? "?"+new URLSearchParams(params).toString() : ""),
        url: "/expenses" + (params ? "?"+queryString.stringify(flatten(params), {encode:false}) : ""),
        method: "GET",
      }),
      providesTags: ["Expenses"],
      // keepUnusedDataFor: 1,
    }),
    addExpenses: builder.mutation({
      query: (payload) => ({
        url: "/expenses/create",
        method: "POST",
        data: payload,
        headers: { "Content-Type": "multipart/form-data" },
      }),
      invalidatesTags: ["Expenses"],
    }),
    updateExpenses: builder.mutation({
      query: (payload) => ({
        url: "/expenses/update",
        method: "POST",
        data: payload,
        headers: { "Content-Type": "multipart/form-data" },
      }),
      invalidatesTags: ["Expenses"],
    }),
    deleteExpenses: builder.mutation({
      query: (payload) => ({
        url: "/expenses/delete",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["Expenses"],
    }),

    // MData -Brand
    getMDataBrand: builder.query({
      query: (params) => ({
        url: "/mdata/brands" + (params ? "?"+new URLSearchParams(params).toString() : ""),
        method: "GET",
      }),
      providesTags: ["MDataBrand"],
      // keepUnusedDataFor: 1,
    }),
    addMDataBrand: builder.mutation({
      query: (payload) => ({
        url: "/mdata/brands/create",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["MDataBrand"],
    }),
    updateMDataBrand: builder.mutation({
      query: (payload) => ({
        url: "/mdata/brands/update",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["MDataBrand"],
    }),
    deleteMDataBrand: builder.mutation({
      query: (payload) => ({
        url: "/mdata/brands/delete",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["MDataBrand"],
    }),

    // MData -Client
    getMDataClient: builder.query({
      query: (params) => ({
        url: "/mdata/clients" + (params ? "?"+new URLSearchParams(params).toString() : ""),
        method: "GET",
      }),
      providesTags: ["MDataClient"],
      // keepUnusedDataFor: 1,
    }),
    addMDataClient: builder.mutation({
      query: (payload) => ({
        url: "/mdata/clients/create",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["MDataClient"],
    }),
    updateMDataClient: builder.mutation({
      query: (payload) => ({
        url: "/mdata/clients/update",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["MDataClient"],
    }),
    deleteMDataClient: builder.mutation({
      query: (payload) => ({
        url: "/mdata/clients/delete",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["MDataClient"],
    }),

    // MData -Product
    getMDataProduct: builder.query({
      query: (params) => ({
        url: "/mdata/products" + (params ? "?"+new URLSearchParams(params).toString() : ""),
        method: "GET",
      }),
      providesTags: ["MDataProduct"],
      // keepUnusedDataFor: 1,
    }),
    addMDataProduct: builder.mutation({
      query: (payload) => ({
        url: "/mdata/products/create",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["MDataProduct"],
    }),
    updateMDataProduct: builder.mutation({
      query: (payload) => ({
        url: "/mdata/products/update",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["MDataProduct"],
    }),
    deleteMDataProduct: builder.mutation({
      query: (payload) => ({
        url: "/mdata/products/delete",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["MDataProduct"],
    }),

    // MData -Staff
    getMDataStaff: builder.query({
      query: (params) => ({
        url: "/mdata/staff" + (params ? "?"+new URLSearchParams(params).toString() : ""),
        method: "GET",
      }),
      providesTags: ["MDataStaff"],
      // keepUnusedDataFor: 1,
    }),
    addMDataStaff: builder.mutation({
      query: (payload) => ({
        url: "/mdata/staff/create",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["MDataStaff"],
    }),
    updateMDataStaff: builder.mutation({
      query: (payload) => ({
        url: "/mdata/staff/update",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["MDataStaff"],
    }),
    deleteMDataStaff: builder.mutation({
      query: (payload) => ({
        url: "/mdata/staff/delete",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["MDataStaff"],
    }),

    // MData -Partner
    getMDataPartner: builder.query({
      query: (params) => ({
        url: "/mdata/partner" + (params ? "?"+new URLSearchParams(params).toString() : ""),
        method: "GET",
      }),
      providesTags: ["MDataPartner"],
      // keepUnusedDataFor: 1,
    }),
    addMDataPartner: builder.mutation({
      query: (payload) => ({
        url: "/mdata/partner/create",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["MDataPartner"],
    }),
    updateMDataPartner: builder.mutation({
      query: (payload) => ({
        url: "/mdata/partner/update",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["MDataPartner"],
    }),
    deleteMDataPartner: builder.mutation({
      query: (payload) => ({
        url: "/mdata/partner/delete",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["MDataPartner"],
    }),

    // MData -Vendor
    getMDataVendor: builder.query({
      query: (params) => ({
        url: "/mdata/vendor" + (params ? "?"+new URLSearchParams(params).toString() : ""),
        method: "GET",
      }),
      providesTags: ["MDataVendor"],
      // keepUnusedDataFor: 1,
    }),
    addMDataVendor: builder.mutation({
      query: (payload) => ({
        url: "/mdata/vendor/create",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["MDataVendor"],
    }),
    updateMDataVendor: builder.mutation({
      query: (payload) => ({
        url: "/mdata/vendor/update",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["MDataVendor"],
    }),
    deleteMDataVendor: builder.mutation({
      query: (payload) => ({
        url: "/mdata/vendor/delete",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["MDataVendor"],
    }),

    // MData -Expenses
    getMDataTypeOfExpenses: builder.query({
      query: (params) => ({
        url: "/mdata/type-of-expenses" + (params ? "?"+new URLSearchParams(params).toString() : ""),
        method: "GET",
      }),
      providesTags: ["MDataTypeOfExpenses"],
      // keepUnusedDataFor: 1,
    }),
    addMDataTypeOfExpenses: builder.mutation({
      query: (payload) => ({
        url: "/mdata/type-of-expenses/create",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["MDataTypeOfExpenses"],
    }),
    updateMDataTypeOfExpenses: builder.mutation({
      query: (payload) => ({
        url: "/mdata/type-of-expenses/update",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["MDataTypeOfExpenses"],
    }),
    deleteMDataTypeOfExpenses: builder.mutation({
      query: (payload) => ({
        url: "/mdata/type-of-expenses/delete",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["MDataTypeOfExpenses"],
    }),

    // Access Control - Role
    getAccessControlRole: builder.query({
      query: (params) => ({
        url: "/access-control/role" + (params ? "?"+new URLSearchParams(params).toString() : ""),
        method: "GET",
      }),
      providesTags: ["AccessControlRole"],
      // keepUnusedDataFor: 1,
    }),
    addAccessControlRole: builder.mutation({
      query: (payload) => ({
        url: "/access-control/role/create",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["AccessControlRole"],
    }),
    updateAccessControlRole: builder.mutation({
      query: (payload) => ({
        url: "/access-control/role/update",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["AccessControlRole"],
    }),
    deleteAccessControlRole: builder.mutation({
      query: (payload) => ({
        url: "/access-control/role/delete",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["AccessControlRole"],
    }),

    // Access Control - Permission
    getAccessControlPermission: builder.query({
      query: (params) => ({
        url: "/access-control/permission" + (params ? "?"+new URLSearchParams(params).toString() : ""),
        method: "GET",
      }),
      providesTags: ["AccessControlPermission"],
      // keepUnusedDataFor: 1,
    }),
    addAccessControlPermission: builder.mutation({
      query: (payload) => ({
        url: "/access-control/permission/create",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["AccessControlPermission"],
    }),
    updateAccessControlPermission: builder.mutation({
      query: (payload) => ({
        url: "/access-control/permission/update",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["AccessControlPermission"],
    }),
    deleteAccessControlPermission: builder.mutation({
      query: (payload) => ({
        url: "/access-control/permission/delete",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["AccessControlPermission"],
    }),

    // Access Control - User
    getAccessControlUser: builder.query({
      query: (params) => ({
        url: "/access-control/users" + (params ? "?"+new URLSearchParams(params).toString() : ""),
        method: "GET",
      }),
      providesTags: ["AccessControlUser"],
      // keepUnusedDataFor: 1,
    }),
    addAccessControlUser: builder.mutation({
      query: (payload) => ({
        url: "/access-control/users/create",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["AccessControlUser"],
    }),
    updateAccessControlUser: builder.mutation({
      query: (payload) => ({
        url: "/access-control/users/update",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["AccessControlUser"],
    }),
    deleteAccessControlUser: builder.mutation({
      query: (payload) => ({
        url: "/access-control/users/delete",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["AccessControlUser"],
    }),

    // Notification
    getNotification: builder.query({
      query: (params) => ({
        url: "/user/notification" + (params ? "?"+new URLSearchParams(params).toString() : ""),
        method: "GET",
      }),
      providesTags: ["Notification"],
      // keepUnusedDataFor: 1,
    }),
    addNotification: builder.mutation({
      query: (payload) => ({
        url: "/user/notification/create",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["Notification"],
    }),
    updateNotification: builder.mutation({
      query: (payload) => ({
        url: "/user/notification/update",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["Notification"],
    }),
    deleteNotification: builder.mutation({
      query: (payload) => ({
        url: "/user/notification/delete",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["Notification"],
    }),

    // Deposit
    dashboardDeposit: builder.query({
      query: (params) => ({
        url: "/deposits/dashboard" + (params ? "?"+new URLSearchParams(params).toString() : ""),
        method: "GET",
      }),
      providesTags: ["Deposit"],
      // keepUnusedDataFor: 1,
    }),
    getDeposit: builder.query({
      query: (params) => ({
        url: "/deposits" + (params ? "?"+new URLSearchParams(params).toString() : ""),
        method: "GET",
      }),
      providesTags: ["Deposit"],
      // keepUnusedDataFor: 1,
    }),
    addDeposit: builder.mutation({
      query: (payload) => ({
        url: "/deposits/create",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["Deposit"],
    }),
    updateDeposit: builder.mutation({
      query: (payload) => ({
        url: "/deposits/update",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["Deposit"],
    }),
    deleteDeposit: builder.mutation({
      query: (payload) => ({
        url: "/deposits/delete",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["Deposit"],
    }),

    // BCA
    dashboardBalance: builder.query({
      query: (params) => ({
        url: "/deposits/dashboard" + (params ? "?"+new URLSearchParams(params).toString() : ""),
        method: "GET",
      }),
      providesTags: ["Balance"],
      // keepUnusedDataFor: 1,
    }),
    getBalance: builder.query({
      query: (params) => ({
        url: "/deposits" + (params ? "?"+new URLSearchParams(params).toString() : ""),
        method: "GET",
      }),
      providesTags: ["Balance"],
      // keepUnusedDataFor: 1,
    }),
    addBalance: builder.mutation({
      query: (payload) => ({
        url: "/deposits/create",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["Balance"],
    }),
    updateBalance: builder.mutation({
      query: (payload) => ({
        url: "/deposits/update",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["Balance"],
    }),
    deleteBalance: builder.mutation({
      query: (payload) => ({
        url: "/deposits/delete",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["Balance"],
    }),
    getBalanceInquiryBCA: builder.query({
      query: (payload) => ({
        url: "/bca/getBalanceInquiry",
        method: "POST",
        data: payload,
      }),
      // providesTags: ["BCA"],
      // keepUnusedDataFor: 1,
    }),
    getBankStatementBCA: builder.query({
      query: (payload) => ({
        url: "/bca/getBankStatement",
        method: "POST",
        data: payload,
      }),
      // providesTags: ["BCA"],
      // keepUnusedDataFor: 1,
    }),

    // NKB Customer
    getNKBCustomer: builder.query({
      // query: (params) =>
      //   "nkb/customer" +
      //   (params ? "?"+new URLSearchParams(params).toString() : ""),
      // providesTags: ["NKBCustomer"],
      query: (params) => ({
        url: "/nkb/customer" + (params ? "?"+new URLSearchParams(params).toString() : ""),
        method: "GET",
      }),
      // keepUnusedDataFor: 60 * 60,
    }),

    // NKB Wishlist
    getNKBWishlist: builder.query({
      // query: (params) =>
      //   "nkb/wishlist" +
      //   (params ? "?" + "?"+new URLSearchParams(params).toString() : ""),
      query: (params) => ({
        url: "/nkb/wishlist" + (params ? "?"+new URLSearchParams(params).toString() : ""),
        method: "GET",
      }),
      providesTags: ["NKBWishlist"],
      // keepUnusedDataFor: 60 * 60,
    }),

    // File Download
    downloadFile: builder.mutation({
      query: (payload) => ({
        url: "/download",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["FileDownload"],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  // NKB Menu
  useGetMenuQuery,

  // NKB Dashboard
  useGetDashboardQuery,

  // NKB Order
  useGetOrderQuery,
  useAddOrderMutation,
  useUpdateOrderMutation,
  useDeleteOrderMutation,

  // NKB Expenses
  useGetExpensesQuery,
  useAddExpensesMutation,
  useUpdateExpensesMutation,
  useDeleteExpensesMutation,

  // MData - Brand
  useGetMDataBrandQuery,
  useAddMDataBrandMutation,
  useUpdateMDataBrandMutation,
  useDeleteMDataBrandMutation,

  // MData - Client
  useGetMDataClientQuery,
  useAddMDataClientMutation,
  useUpdateMDataClientMutation,
  useDeleteMDataClientMutation,

  // MData - Product
  useGetMDataProductQuery,
  useAddMDataProductMutation,
  useUpdateMDataProductMutation,
  useDeleteMDataProductMutation,

  // MData - Staff
  useGetMDataStaffQuery,
  useAddMDataStaffMutation,
  useUpdateMDataStaffMutation,
  useDeleteMDataStaffMutation,

  // MData - Partner
  useGetMDataPartnerQuery,
  useAddMDataPartnerMutation,
  useUpdateMDataPartnerMutation,
  useDeleteMDataPartnerMutation,

  // MData - Vendor
  useGetMDataVendorQuery,
  useAddMDataVendorMutation,
  useUpdateMDataVendorMutation,
  useDeleteMDataVendorMutation,

  // MData - Type of Expenses
  useGetMDataTypeOfExpensesQuery,
  useAddMDataTypeOfExpensesMutation,
  useUpdateMDataTypeOfExpensesMutation,
  useDeleteMDataTypeOfExpensesMutation,

  // Access Control - Role
  useGetAccessControlRoleQuery,
  useAddAccessControlRoleMutation,
  useUpdateAccessControlRoleMutation,
  useDeleteAccessControlRoleMutation,

  // Access Control - Permission
  useGetAccessControlPermissionQuery,
  useAddAccessControlPermissionMutation,
  useUpdateAccessControlPermissionMutation,
  useDeleteAccessControlPermissionMutation,

  // Access Control - User
  useGetAccessControlUserQuery,
  useAddAccessControlUserMutation,
  useUpdateAccessControlUserMutation,
  useDeleteAccessControlUserMutation,

  // Notification
  useGetNotificationQuery,
  useAddNotificationMutation,
  useUpdateNotificationMutation,
  useDeleteNotificationMutation,

  // Deposit
  useDashboardDepositQuery,
  useGetDepositQuery,
  useAddDepositMutation,
  useUpdateDepositMutation,
  useDeleteDepositMutation,

  // BCA
  useDashboardBalanceQuery,
  useGetBalanceQuery,
  useAddBalanceMutation,
  useUpdateBalanceMutation,
  useDeleteBalanceMutation,

  useGetBalanceInquiryBCAQuery,
  useGetBankStatementBCAQuery,
  useLazyGetBankStatementBCAQuery,

  // File Download
  useDownloadFileMutation,

  // NKB
  useGetNKBCustomerQuery,
  useGetNKBWishlistQuery,
} = api;
